// src/components/common/PasswordField.tsx
// Reusable password input field with visibility toggle
import React, { useState } from 'react';
import {
  TextField,
  IconButton,
  InputAdornment,
  TextFieldProps,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

interface PasswordFieldProps extends Omit<TextFieldProps, 'type'> {
  showStrengthIndicator?: boolean;
  onStrengthChange?: (strength: number) => void;
  // Underlying field to render — defaults to TextField (LoginPage), pass
  // StyledTextField to match a dialog's own chrome (UserDialog).
  component?: React.ElementType;
}

const PasswordField: React.FC<PasswordFieldProps> = ({
  showStrengthIndicator = false,
  onStrengthChange,
  value,
  onChange,
  component: Field = TextField,
  ...textFieldProps
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  // Calculate password strength if indicator is enabled
  React.useEffect(() => {
    if (
      showStrengthIndicator &&
      onStrengthChange &&
      typeof value === 'string'
    ) {
      let strength = 0;
      const password = value as string;

      if (password.length >= 8) strength++;
      if (password.length >= 12) strength++;
      if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
      if (/\d/.test(password)) strength++;
      if (/[^a-zA-Z0-9]/.test(password)) strength++;

      onStrengthChange(Math.min(strength, 4));
    }
  }, [value, showStrengthIndicator, onStrengthChange]);

  return (
    <Field
      {...textFieldProps}
      type={showPassword ? 'text' : 'password'}
      value={value}
      onChange={onChange}
      InputProps={{
        ...textFieldProps.InputProps,
        endAdornment: (
          <InputAdornment position="end">
            {textFieldProps.InputProps?.endAdornment}
            <IconButton
              aria-label={showPassword ? 'hide password' : 'show password'}
              onClick={handleClickShowPassword}
              onMouseDown={handleMouseDownPassword}
              edge="end"
              disabled={textFieldProps.disabled}
              tabIndex={-1}
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

export default PasswordField;
